import type { ComponentType } from 'react';
import type { WizardStepProps } from '../engine/types';
import { STEP_KEYS } from './stepKeys';

// Re-export STEP_KEYS for convenience
export { STEP_KEYS, type StepKey } from './stepKeys';

// ============================================================================
// STEP REGISTRY - Singleton for managing step components
// ============================================================================

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type StepComponent = ComponentType<WizardStepProps<any>>;

class StepRegistryManager {
  private steps: Map<string, StepComponent> = new Map();
  private initializedSports: Set<string> = new Set();

  /**
   * Register a step component
   */
  register(key: string, component: StepComponent): void {
    this.steps.set(key, component);
  }

  /**
   * Register multiple step components
   */
  registerMany(entries: Record<string, StepComponent>): void {
    Object.entries(entries).forEach(([key, component]) => {
      this.register(key, component);
    });
  }

  /**
   * Get a step component by key
   */
  get(key: string): StepComponent | undefined {
    return this.steps.get(key);
  }

  /**
   * Get a step component by key (throws if not found)
   */
  getOrThrow(key: string): StepComponent {
    const component = this.steps.get(key);
    if (!component) {
      throw new Error(`Step component not found for key: ${key}`);
    }
    return component;
  }

  /**
   * Check if a step is registered
   */
  has(key: string): boolean {
    return this.steps.has(key);
  }

  /**
   * Mark a sport as initialized (all its steps are registered)
   */
  markSportInitialized(sportId: string): void {
    this.initializedSports.add(sportId.toUpperCase());
  }

  /**
   * Check if a sport's steps are initialized
   */
  isSportInitialized(sportId: string): boolean {
    return this.initializedSports.has(sportId.toUpperCase());
  }

  /**
   * Get all registered step keys
   */
  getAllKeys(): string[] {
    return Array.from(this.steps.keys());
  }

  /**
   * Clear all registrations (useful for testing)
   */
  clear(): void {
    this.steps.clear();
    this.initializedSports.clear();
  }
}

// Singleton instance
export const stepRegistry = new StepRegistryManager();

// ============================================================================
// PLACEHOLDER COMPONENTS
// ============================================================================

export function PlaceholderStep({ draft }: WizardStepProps<unknown>) {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-8 text-center">
        <p className="text-gray-500">
          Este paso está en desarrollo.
        </p>
        <p className="mt-2 text-sm text-gray-400">
          Flow: {draft.flowId}
        </p>
      </div>
    </div>
  );
}

export function NotFoundStep({ draft }: WizardStepProps<unknown>) {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="rounded-lg border border-red-200 bg-red-50 p-8 text-center">
        <p className="text-red-600">
          Paso no encontrado
        </p>
        <p className="mt-2 text-sm text-red-400">
          El componente para este paso no está registrado.
        </p>
        <p className="mt-1 text-xs text-red-300">
          Step ID: {draft.currentStepId}
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// AUTO-REGISTRATION
// ============================================================================

// Import all step components
import { SportStep } from '../sports/shared/steps/SportStep';
import { BasicInfoStep } from '../sports/shared/steps/BasicInfoStep';
import { PricingStep } from '../sports/shared/steps/PricingStep';
import { ClubSelectionStep } from '../sports/shared/steps/ClubSelectionStep';
import { ScheduleSelectionStep } from '../sports/shared/steps/ScheduleSelectionStep';
import { TournamentConfigStep } from '../sports/shared/steps/TournamentConfigStep';
import { ClubSchedulesStep } from '../sports/shared/steps/ClubSchedulesStep';
import { SummaryStep } from '../sports/shared/steps/SummaryStep';

import { TeamConfigStep as PadelTeamConfigStep } from '../sports/padel/steps/TeamConfigStep';
import { FixtureConfigStep as PadelFixtureConfigStep } from '../sports/padel/steps/FixtureConfigStep';
import { ScheduleStep as PadelScheduleStep } from '../sports/padel/steps/ScheduleStep';

// TODO: Uncomment when ready to implement tennis and squash wizards
// import { TeamConfigStep as TennisTeamConfigStep } from '../sports/tennis/steps/TeamConfigStep';
// import { FixtureConfigStep as TennisFixtureConfigStep } from '../sports/tennis/steps/FixtureConfigStep';
// import { ScheduleStep as TennisScheduleStep } from '../sports/tennis/steps/ScheduleStep';

// import { TeamConfigStep as SquashTeamConfigStep } from '../sports/squash/steps/TeamConfigStep';
// import { FixtureConfigStep as SquashFixtureConfigStep } from '../sports/squash/steps/FixtureConfigStep';
// import { ScheduleStep as SquashScheduleStep } from '../sports/squash/steps/ScheduleStep';

// Register all steps
stepRegistry.registerMany({
  // Shared steps
  [STEP_KEYS.SHARED_SPORT]: SportStep,
  [STEP_KEYS.SHARED_BASIC_INFO]: BasicInfoStep,
  [STEP_KEYS.SHARED_PRICING]: PricingStep,
  [STEP_KEYS.SHARED_CLUB_SELECTION]: ClubSelectionStep,
  [STEP_KEYS.SHARED_SCHEDULE_SELECTION]: ScheduleSelectionStep,
  [STEP_KEYS.SHARED_TOURNAMENT_CONFIG]: TournamentConfigStep,
  [STEP_KEYS.SHARED_CLUB_SCHEDULES]: ClubSchedulesStep,
  [STEP_KEYS.SHARED_SUMMARY]: SummaryStep,
  
  // Padel steps
  [STEP_KEYS.PADEL_TEAM_CONFIG]: PadelTeamConfigStep,
  [STEP_KEYS.PADEL_FIXTURE_CONFIG]: PadelFixtureConfigStep,
  [STEP_KEYS.PADEL_SCHEDULE]: PadelScheduleStep,
  
  // TODO: Uncomment when ready to implement tennis and squash wizards
  // Tennis steps
  // [STEP_KEYS.TENNIS_TEAM_CONFIG]: TennisTeamConfigStep,
  // [STEP_KEYS.TENNIS_FIXTURE_CONFIG]: TennisFixtureConfigStep,
  // [STEP_KEYS.TENNIS_SCHEDULE]: TennisScheduleStep,
  
  // Squash steps
  // [STEP_KEYS.SQUASH_TEAM_CONFIG]: SquashTeamConfigStep,
  // [STEP_KEYS.SQUASH_FIXTURE_CONFIG]: SquashFixtureConfigStep,
  // [STEP_KEYS.SQUASH_SCHEDULE]: SquashScheduleStep,
});

// Mark initialized sports
stepRegistry.markSportInitialized('PADEL');
// TODO: Uncomment when ready
// stepRegistry.markSportInitialized('TENNIS');
// stepRegistry.markSportInitialized('SQUASH');
